import { useState, useCallback } from 'react';
import { userAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';

/**
 * Custom hook for managing user form state, validation, and submission
 */
export const useUserForm = (selectedUser, onSuccess, getErrorMessage) => {
  const { showSuccess, showError } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    is_active: true
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Reset form data
   */
  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      username: '',
      email: '',
      password: '',
      is_active: true
    });
    setFormErrors({});
  }, []);

  /**
   * Initialize form with user data for editing
   */
  const initializeForm = useCallback((user) => {
    if (user) {
      setFormData({
        name: user.name || '',
        username: user.username || '',
        email: user.email || '',
        password: '',
        is_active: user.is_active !== undefined ? user.is_active : true
      });
      setFormErrors({});
    } else {
      resetForm();
    }
  }, [resetForm]);

  /**
   * Handle form input changes
   */
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  }, [formErrors]);


  /**
   * Validate form data
   */
  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }

    if (!selectedUser && !formData.password.trim()) {
      errors.password = 'Password is required for new users';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, selectedUser]);

  /**
   * Handle API validation errors
   */
  const handleApiError = useCallback((err, defaultMessage) => {
    console.error('API Error Details:', {
      message: err.message,
      status: err.status,
      data: err.data,
      errors: err.errors
    });

    if (err.errors && err.errors.length > 0) {
      const fieldErrors = {};
      err.errors.forEach(error => {
        if (error.path) {
          fieldErrors[error.path] = error.msg || error.message;
        }
      });
      setFormErrors(fieldErrors);
      showError('Please fix the validation errors below');
      return true; // Indicates validation errors were handled
    } else {
      const errorMessage = getErrorMessage(err, defaultMessage);
      setFormErrors({ submit: errorMessage });
      showError(errorMessage);
      return false; // Indicates general error was handled
    }
  }, [getErrorMessage, showError]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const userData = {
        name: formData.name.trim(),
        username: formData.username.trim(),
        email: formData.email.trim(),
        role: 'admin', // Always set to admin
        is_active: formData.is_active
      };

      // Only include password if it's provided
      if (formData.password.trim()) {
        userData.password = formData.password;
      }

      if (selectedUser) {
        // Update existing user
        await userAPI.update(selectedUser.id, userData);
        showSuccess(`User "${formData.name}" updated successfully!`);
      } else {
        // Create new user
        await userAPI.create(userData);
        showSuccess(`User "${formData.name}" created successfully!`);
      }

      // Reset form and trigger success callback
      resetForm();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      handleApiError(err, 'Failed to save user');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, selectedUser, validateForm, handleApiError, showSuccess, resetForm, onSuccess]);

  return {
    formData,
    formErrors,
    isSubmitting,
    setFormData,
    resetForm,
    initializeForm,
    handleInputChange,
    handleSubmit
  };
};

